/**
 * Validation utilities for ensuring data conforms to our model interfaces
 */

import type { Project } from "./project";
import type { Process, Task } from "./flowchart";
import type { WorkspaceSection } from "./workspace";

/**
 * Validates that a JSON object conforms to the Project interface
 */
export function validateProject(data: any): data is Project {
  if (typeof data !== "object" || data === null) return false;

  // Check required fields
  if (typeof data.id !== "string") return false;
  if (typeof data.codicent !== "string") return false;
  if (typeof data.name !== "string") return false;
  if (typeof data.workspaces !== "object") return false;
  if (!Array.isArray(data.teammates)) return false;

  return true;
}

/**
 * Validates that a JSON object conforms to the Process interface
 */
export function validateProcess(data: any): data is Process {
  if (typeof data !== "object" || data === null) return false;

  if (typeof data.id !== "string") return false;
  if (typeof data.name !== "string") return false;
  if (!Array.isArray(data.stages)) return false;

  return true;
}

/**
 * Validates that a JSON object conforms to the Task interface
 */
export function validateTask(data: any): data is Task {
  if (typeof data !== "object" || data === null) return false;

  if (!data.id || (typeof data.id !== "string" && typeof data.id !== "number")) return false;
  if (typeof data.name !== "string") return false;

  return true;
}

/**
 * Type guard to check if data structure has flowchart data
 */
export function hasFlowchartData(data: any): data is { data: { processes: Process[] } } {
  return data?.data?.processes && Array.isArray(data.data.processes);
}

/**
 * Type guard to check if workspace section has tabs
 */
export function hasTabsStructure(section: any): section is WorkspaceSection & { tabs: any[] } {
  return section?.tabs && Array.isArray(section.tabs);
}

/**
 * Type guard to check if workspace section has direct data
 */
export function hasDirectData(section: any): section is WorkspaceSection & { data: any } {
  return section?.data !== undefined;
}

/**
 * Validates that a JSON object is a valid partial project update
 * More permissive than validateProject - allows missing required fields
 */
export function validatePartialProject(data: any): data is Partial<Project> {
  if (typeof data !== "object" || data === null) return false;
  
  // Check that if fields exist, they have correct types
  if (data.id !== undefined && typeof data.id !== "string") return false;
  if (data.codicent !== undefined && typeof data.codicent !== "string") return false;
  if (data.name !== undefined && typeof data.name !== "string") return false;
  if (data.workspaces !== undefined && typeof data.workspaces !== "object") return false;
  if (data.teammates !== undefined && !Array.isArray(data.teammates)) return false;
  
  return true;
}

/**
 * Deep merges a partial project update into an existing project
 * @param target - The existing project to merge into
 * @param source - The partial update to merge from
 * @returns Merged project
 */
export function mergePartialProject(target: Project, source: Partial<Project>): Project {
  // Deep clone to avoid mutating the original
  const result = JSON.parse(JSON.stringify(target));
  
  // Helper function for deep merging
  const deepMerge = (targetObj: any, sourceObj: any) => {
    for (const key in sourceObj) {
      if (sourceObj[key] && typeof sourceObj[key] === 'object' && !Array.isArray(sourceObj[key])) {
        // Nested object - merge recursively
        if (!targetObj[key] || typeof targetObj[key] !== 'object') {
          targetObj[key] = {};
        }
        deepMerge(targetObj[key], sourceObj[key]);
      } else {
        // Primitive value or array - replace directly
        targetObj[key] = sourceObj[key];
      }
    }
  };
  
  deepMerge(result, source);
  return result;
}
